import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Film, Mail, User, Lock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Vul je e-mailadres en wachtwoord in');
      return;
    }

    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      toast.success('Welkom terug!');
      navigate('/');
    } else {
      toast.error(result.error || 'Inloggen mislukt');
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Film className="w-6 h-6 text-white" />
            </div>
            <span className="text-3xl font-display font-bold tracking-tight">Episo</span>
          </Link>

          <h2 className="text-2xl font-display font-bold text-center mb-2">
            Welkom terug
          </h2>
          <p className="text-gray-400 text-center mb-8">
            Log in om je kijkgedrag bij te houden
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-gray-300">E-mailadres</Label>
              <div className="mt-2 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="jouw@email.nl"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                  className="pl-10 bg-white/5 border-white/10 focus:border-indigo-500"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-300">Wachtwoord</Label>
              <div className="mt-2 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="password-input"
                  className="pl-10 bg-white/5 border-white/10 focus:border-indigo-500"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-indigo-600 hover:bg-indigo-700 py-6 rounded-xl font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Inloggen...
                </>
              ) : (
                'Inloggen'
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-400">
            Nog geen account?{' '}
            <Link to="/register" className="text-indigo-400 hover:text-indigo-300 font-medium">
              Registreren
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1524629802380-04f8ec63f471?crop=entropy&cs=srgb&fm=jpg&q=85)' 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/50 to-transparent" />
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Vul je e-mailadres in');
      return;
    }

    if (!password || password.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 tekens zijn');
      return;
    }

    if (password !== password2) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }

    setLoading(true);
    const result = await register(email, name, password);
    setLoading(false);

    if (result.success) {
      toast.success('Account aangemaakt!');
      navigate('/');
    } else {
      toast.error(result.error || 'Registratie mislukt');
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Film className="w-6 h-6 text-white" />
            </div>
            <span className="text-3xl font-display font-bold tracking-tight">Episo</span>
          </Link>

          <h2 className="text-2xl font-display font-bold text-center mb-2">
            Account aanmaken
          </h2>
          <p className="text-gray-400 text-center mb-8">
            Begin met het bijhouden van je kijkgedrag
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-gray-300">Naam (optioneel)</Label>
              <div className="mt-2 relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Je naam"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="name-input"
                  className="pl-10 bg-white/5 border-white/10 focus:border-indigo-500"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-300">E-mailadres</Label>
              <div className="mt-2 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="jouw@email.nl"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                  className="pl-10 bg-white/5 border-white/10 focus:border-indigo-500"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-300">Wachtwoord</Label>
              <div className="mt-2 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Minimaal 8 tekens"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="password-input"
                  className="pl-10 bg-white/5 border-white/10 focus:border-indigo-500"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password2" className="text-gray-300">Wachtwoord herhalen</Label>
              <div className="mt-2 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password2"
                  type="password"
                  placeholder="Herhaal wachtwoord"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  data-testid="password2-input"
                  className="pl-10 bg-white/5 border-white/10 focus:border-indigo-500"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full bg-indigo-600 hover:bg-indigo-700 py-6 rounded-xl font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Account aanmaken...
                </>
              ) : (
                'Account aanmaken'
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-400">
            Heb je al een account?{' '}
            <Link to="/login" className="text-indigo-400 hover:text-indigo-300 font-medium">
              Inloggen
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1524629802380-04f8ec63f471?crop=entropy&cs=srgb&fm=jpg&q=85)' 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/50 to-transparent" />
      </div>
    </div>
  );
};

export default LoginPage;
